import { cn } from '../../utils';
import { X } from 'lucide-react';

export const Button = ({ children, variant = 'primary', size = 'md', className, ...props }) => {
  const variants = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    outline: 'btn-outline',
    danger: 'btn-danger',
    ghost: 'hover:bg-gray-100 text-gray-600',
  };
  const sizes = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };
  return (
    <button className={cn('btn', variants[variant], sizes[size], className)} {...props}>
      {children}
    </button>
  );
};

export const Card = ({ children, className, ...props }) => (
  <div className={cn('card', className)} {...props}>{children}</div>
);

export const Input = ({ label, error, className, ...props }) => (
  <div className="space-y-1">
    {label && <label className="label">{label}</label>}
    <input className={cn('input', error && 'border-red-500', className)} {...props} />
    {error && <p className="text-xs text-red-500">{error}</p>}
  </div>
);

export const Select = ({ label, options, className, ...props }) => (
  <div className="space-y-1">
    {label && <label className="label">{label}</label>}
    <select className={cn('input', className)} {...props}>
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

export const Textarea = ({ label, error, className, ...props }) => (
  <div className="space-y-1">
    {label && <label className="label">{label}</label>}
    <textarea className={cn('input min-h-[100px]', error && 'border-red-500', className)} {...props} />
    {error && <p className="text-xs text-red-500">{error}</p>}
  </div>
);

export const Badge = ({ children, variant = 'info', className }) => {
  const variants = {
    success: 'badge-success',
    warning: 'badge-warning',
    danger: 'badge-danger',
    info: 'badge-info',
  };
  return <span className={cn('badge', variants[variant], className)}>{children}</span>;
};

export const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  const sizes = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className={cn('relative bg-white rounded-xl shadow-xl w-full', sizes[size])}>
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 max-h-[70vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

export const Spinner = ({ size = 'md' }) => {
  const sizes = { sm: 'w-4 h-4', md: 'w-6 h-6', lg: 'w-8 h-8' };
  return (
    <div className={cn('animate-spin rounded-full border-2 border-primary-600 border-t-transparent', sizes[size])} />
  );
};

export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="text-center py-12">
    <Icon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-gray-900 mb-1">{title}</h3>
    <p className="text-gray-500 mb-4">{description}</p>
    {action}
  </div>
);

export const StatCard = ({ icon: Icon, label, value, subtext, trend, color = 'primary' }) => {
  const colors = {
    primary: 'bg-primary-50 text-primary-600',
    amber: 'bg-amber-50 text-amber-600',
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    red: 'bg-red-50 text-red-600',
    green: 'bg-green-50 text-green-600',
  };
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div className={cn('p-2 rounded-lg', colors[color])}>
          <Icon className="w-5 h-5" />
        </div>
        {trend && (
          <span className={cn('text-xs font-medium', trend > 0 ? 'text-green-600' : 'text-red-600')}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <div className="mt-3">
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-sm text-gray-500">{label}</p>
        {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
      </div>
    </Card>
  );
};

export const ProgressBar = ({ value, max, className }) => {
  const percentage = Math.min(100, (value / max) * 100);
  return (
    <div className={cn('h-2 bg-gray-200 rounded-full overflow-hidden', className)}>
      <div className="h-full bg-primary-500 rounded-full transition-all duration-500" style={{ width: `${percentage}%` }} />
    </div>
  );
};

export const Tabs = ({ tabs, activeTab, onChange }) => (
  <div className="flex gap-1 border-b">
    {tabs.map(tab => (
      <button
        key={tab.id}
        onClick={() => onChange(tab.id)}
        className={cn(
          'px-4 py-2 font-medium text-sm transition-colors',
          activeTab === tab.id
            ? 'text-primary-600 border-b-2 border-primary-600'
            : 'text-gray-500 hover:text-gray-700'
        )}
      >
        {tab.label}
      </button>
    ))}
  </div>
);
